/**
 * TanStack Query hooks for QA Comments
 *
 * Provides hooks for listing, creating, updating, and deleting
 * QA comments on blog content items.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import { qaCommentService } from '../services/qaCommentService';
import { qaReviewKeys } from './useQAReviews';
import type {
  QACommentCreate,
  QACommentUpdate,
  QACommentListResponse,
} from '../types/qaReview';

export const qaCommentKeys = {
  all: ['qa-comments'] as const,
  lists: () => [...qaCommentKeys.all, 'list'] as const,
  list: (contentId: string, tenantId: string) =>
    [...qaCommentKeys.lists(), contentId, tenantId] as const,
};

/**
 * Fetch all comments for a content item.
 */
export function useQAComments(contentId: string, tenantId: string) {
  return useQuery<QACommentListResponse>({
    queryKey: qaCommentKeys.list(contentId, tenantId),
    queryFn: () => qaCommentService.list(contentId, tenantId),
    enabled: !!contentId && !!tenantId,
  });
}

/**
 * Create a new comment on a content item.
 * Invalidates both comments list and QA reviews list (for comment_count badge).
 */
export function useCreateComment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      contentId,
      tenantId,
      data,
    }: {
      contentId: string;
      tenantId: string;
      data: QACommentCreate;
    }) => qaCommentService.create(contentId, tenantId, data),

    onSuccess: (_, { contentId, tenantId }) => {
      queryClient.invalidateQueries({
        queryKey: qaCommentKeys.list(contentId, tenantId),
      });
      // Invalidate QA reviews list so comment_count badge updates
      queryClient.invalidateQueries({ queryKey: qaReviewKeys.lists() });
    },

    onError: () => {
      message.error('Failed to add comment');
    },
  });
}

/**
 * Update an existing comment's text.
 */
export function useUpdateComment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      commentId,
      tenantId,
      data,
    }: {
      commentId: string;
      tenantId: string;
      contentId: string;
      data: QACommentUpdate;
    }) => qaCommentService.update(commentId, tenantId, data),

    onSuccess: (_, { contentId, tenantId }) => {
      queryClient.invalidateQueries({
        queryKey: qaCommentKeys.list(contentId, tenantId),
      });
      // Invalidate reviews too in case latest_comment text changed
      queryClient.invalidateQueries({ queryKey: qaReviewKeys.lists() });
    },

    onError: () => {
      message.error('Failed to update comment');
    },
  });
}

/**
 * Delete a comment.
 */
export function useDeleteComment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      commentId,
      tenantId,
    }: {
      commentId: string;
      tenantId: string;
      contentId: string;
    }) => qaCommentService.delete(commentId, tenantId),

    onSuccess: (_, { contentId, tenantId }) => {
      queryClient.invalidateQueries({
        queryKey: qaCommentKeys.list(contentId, tenantId),
      });
      // Invalidate reviews for comment_count badge
      queryClient.invalidateQueries({ queryKey: qaReviewKeys.lists() });
    },

    onError: () => {
      message.error('Failed to delete comment');
    },
  });
}
